import logger from 'portfolio-shared/util/logging';
import { multivalueCompareFn } from 'portfolio-shared/util/fieldDefUtils';

// Utilities for Nested Values

export const DEFAULT_NEW_LINE_CHAR = '\n';
export const WINDOWS_NEW_LINE_CHAR = '\r\n';
export const DEFAULT_TAB_CHAR = '\t';

export function createNestedValue(value, isRoot = false) {
  return {
    children: [],
    id: null,
    isRootNode: isRoot,
    parent: null,
    value: value,
  };
}

export function isNestedValueAncestorOfNestedValue(possibleAncestor, nv) {
  // don't pay attention to null ids, e.g. if the NVs were created locally and
  // not yet persisted, and therefore don't have ids yet.
  if (!possibleAncestor.id || !nv || !nv.id) {
    return false;
  }
  if (nv.parent && !nv.parent.isRootNode) {
    if (nv.parent.id === possibleAncestor.id) {
      return true;
    } else {
      return possibleAncestor.isNestedValueAncestorOfNestedValue(
        possibleAncestor,
        nv.parent
      );
    }
  }
  return false;
}

export function addChildToNestedValue(child, nestedValue) {
  // guard against creating circular references
  if (!isNestedValueAncestorOfNestedValue(child, nestedValue)) {
    child.parent = nestedValue;
    nestedValue.children.push(child);
  }
}

// flattens the nested values and calculates the tab index
// for each value depending on its depth
export function flattenNestedValues(values, tabIndex) {
  const flat = [];

  values.forEach(value => {
    value.tabIndex = tabIndex;
    flat.push(value);
    if (Array.isArray(value.children) && value.children.length > 0) {
      flat.push(...flattenNestedValues(value.children, tabIndex + 1));
      delete value.children;
    }
    delete value.children;
  });

  return flat;
}

// takes an array of flattened nested values and
// creates an array of strings for output
// tab characters denote the depth of the items
export function nestedValuesToArray(items, returnChar) {
  const result = [];

  items.forEach(item => {
    let tabs = '';
    for (let i = 0; i < item.tabIndex; ++i) {
      tabs += DEFAULT_TAB_CHAR;
    }
    result.push(tabs + item.value + returnChar);
  });

  return result;
}

//
// Note: The following methods are ported as directly as possible
// from flex-shared's classes NestedValue.as, ListEditor.mxml, and
// HierarchicalList.mxml
//

export function parseList(text, fieldDef) {
  if (!text || text.trim() === '') {
    return;
  }

  let items;
  const isWinNewlineDelimited = -1 !== text.indexOf(WINDOWS_NEW_LINE_CHAR);
  if (isWinNewlineDelimited) {
    items = text.split(WINDOWS_NEW_LINE_CHAR);
  } else {
    items = text.split(DEFAULT_NEW_LINE_CHAR);
  }

  if (fieldDef.isHierarchical) {
    const rootNode = createNestedValue('dummy root node', true);
    rootNode.children = fieldDef.nestedPredefinedValues;
    return _parseHierarchicalList(rootNode, items, fieldDef);
  } else {
    // Replace control charcters. These cause a WstxUnexpectedCharException in the SOAP parser
    // Alas, the Flex/ECMA RegEx engine doesn't do Unicode ranges, need to match u00-u1F and u7F individually.
    const pattern = /\x00|\x01|\x02|\x03|\x04|\x05|\x06|\x07|\x08|\x09|\x0A|\x0B|\x0C|\x0D|\x0E|\x0F|\x10|\x11|\x12|\x13|\x14|\x15|\x16|\x17|\x18|\x19|\x1A|\x1B|\x1C|\x1D|\x1E|\x1F|\x7F/g;
    for (let ix = 0; ix < items.length; ix++) {
      items[ix] = items[ix].replace(pattern, '');
    }
    return _parseFlatList(items, fieldDef);
  }
}

function _parseFlatList(items, fieldDef) {
  // trim lines and remove blank lines
  items = items.map(item => item.trim()).filter(item => item.trim() !== '');

  // During development we only tested up to 10k items. With larger lists, like 70k
  // we need to make a few shortcuts or the app becomes unresponsive. Avoid validating,
  // looking for duplicates, and sorting large lists. Rely on the server for this.
  const bulkAdd = items.length > 10000;

  if (!bulkAdd) {
    items.sort(multivalueCompareFn(fieldDef, items[0]));
  }

  return items;
}

function _parseHierarchicalList(rootNode, items, fieldDef) {
  const newValues = nestedValuesFromStrings(items);
  const addedValues = [];

  newValues.forEach(newValue => {
    if (
      !_newValueIsDuplicateOfExistingNestedValueInList(
        newValue.value,
        rootNode.children
      )
    ) {
      addChildToNestedValue(newValue, rootNode);
      addedValues.push(newValue);
    }
  });

  if (addedValues.length > 0) {
    return nestedValuesToServerObjects(addedValues);
  }
}

function _newValueIsDuplicateOfExistingNestedValueInList(newValue, existing) {
  for (let ix = 0; ix < existing.length; ++ix) {
    const nestedValue = existing[ix];
    if (nestedValue.value.toLowerCase() === newValue.toLowerCase()) {
      return true;
    }
  }
  return false;
}

// Parses a list of Nested Values from a list of strings,
// where tabs are assumed to be structural.
// Sorts and culls dupes.
export function nestedValuesFromStrings(
  stringValues,
  tabChar = DEFAULT_TAB_CHAR
) {
  const currParents = []; // parent at each lower level
  let ret = [];

  for (let ix = 0; ix < stringValues.length; ++ix) {
    let val = stringValues[ix];
    let currTabIx = 0;
    while (val.charAt(0) === tabChar) {
      val = val.substr(1);
      ++currTabIx;
    }

    // make sure we do not have parentless nvs except for root ones
    if (currTabIx !== 0 && currParents.length < currTabIx) {
      logger.info('error parsing nested values from strings');
      // TODO: should we be more forgiving and just ignore this subtree?
      return null;
    }

    val.trim();
    if (val.length > 0) {
      const nv = createNestedValue(val);

      if (currTabIx === 0) {
        ret.push(nv);
      } else {
        const parent = currParents[currTabIx - 1];
        addChildToNestedValue(nv, parent);
      }

      // update curr parents
      if (currParents.length === currTabIx) {
        currParents.push(nv);
      } else {
        currParents[currTabIx] = nv;
      }

      // remove any old parents beyond curr ix
      while (currParents.length > currTabIx + 1) {
        currParents.pop();
      }
    }
  }

  // sort and cull dupes
  _sortNVs(ret);
  ret = _cullDupes(ret);

  return ret;
}

function _sortNVs(nvs) {
  nvs.forEach(nv => {
    if (nv.children) {
      _sortNVs(nv.children);
    }
  });
  nvs.sort((a, b) => a.value.localeCompare(b.value));
}

// Return a list of nested values with dupes culled and children coalesced.
function _cullDupes(nestedValues) {
  const ret = [];
  for (let ix = 0; ix < nestedValues.length; ++ix) {
    const nestedValue = nestedValues[ix];
    const peers = [];
    for (let jx = ix + 1; jx < nestedValues.length; ++jx) {
      peers.push(nestedValues[jx]);
    }

    const dupe = _findDupeValueAmongNestedValues(nestedValue, peers);
    if (dupe) {
      // add nested values children to the dupe
      // TODO: we may have to make this method multi-pass we if we really care
      // about proper child coalescing
      dupe.children.push(...nestedValue.children);
      continue;
    } else {
      ret.push(nestedValue);

      // cull children recursively
      nestedValue.children = _cullDupes(nestedValue.children);
    }
  }
  return ret;
}

function _findDupeValueAmongNestedValues(nv, values) {
  for (let ix = 0; ix < values.length; ++ix) {
    if (nv.value === values[ix].value) {
      return values[ix];
    }
  }
  return null;
}

// Walk two nested value lists and make sure the values are equivalent.
// Assumes sorted lists.
export function areNestedValueListsEqual(nvs1, nvs2) {
  let nv1;
  let nv2;

  if (!nvs1 && !nvs2) {
    return true;
  }

  if (nvs1.length !== nvs2.length) {
    return false;
  }

  for (let ix = 0; ix < nvs1.length; ++ix) {
    nv1 = nvs1[ix];
    nv2 = nvs2[ix];
    if (nv1.value !== nv2.value) {
      return false;
    }
    if (!areNestedValueListsEqual(nv1.children, nv2.children)) {
      return false;
    }
  }

  return true;
}

// List of NVs to string
export function nestedValuesArrayToStructuredString(
  nvs,
  newLineChar = DEFAULT_NEW_LINE_CHAR,
  tabChar = DEFAULT_TAB_CHAR
) {
  let ret = '';
  for (let ix = 0; ix < nvs.length; ++ix) {
    const nv = nvs[ix];
    ret += _nestedValueToStructuredString(nv, 0, newLineChar, tabChar);
  }
  return ret;
}

// Single NV to structured string
export function _nestedValueToStructuredString(
  nv,
  tabIx = 0,
  newLineChar = DEFAULT_NEW_LINE_CHAR,
  tabChar = DEFAULT_TAB_CHAR
) {
  let ret = '';
  for (let tx = 0; tx < tabIx; ++tx) {
    ret += tabChar;
  }
  ret += nv.value;
  ret += newLineChar;

  if (nv.children) {
    for (let ix = 0; ix < nv.children.length; ++ix) {
      ret += _nestedValueToStructuredString(
        nv.children[ix],
        tabIx + 1,
        newLineChar,
        tabChar
      );
    }
  }
  return ret;
}

// Converts from a NV to the server-side object format
export function nestedValueToServerObject(nv) {
  const o = {
    children: [],
    id: nv.id,
    parentId: (nv.parentId = nv.parent ? nv.parent.id : null),
    value: nv.value,
  };

  // recursively call toObject() for all children of this nested value
  nv.children.forEach(child =>
    o.children.push(nestedValueToServerObject(child))
  );

  return o;
}

export function nestedValuesToServerObjects(nvs) {
  return nvs.map(nv => nestedValueToServerObject(nv));
}



// WEBPACK FOOTER //
// ./src/util/nestedValues.js